import { SidebarProvider } from "@/components/ui/sidebar";
import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DeviceGrid } from "@/components/devices/DeviceGrid";
import { DeviceHistory } from "@/components/devices/tabs/DeviceHistory";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DeviceAssignmentHandler } from "@/components/auth/DeviceAssignmentHandler";
import { AppSidebar } from "@/components/AppSidebar";
import { Button } from "@/components/ui/button";
import { PlusCircle, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { CustomerOrderDialog } from "@/components/crm/CustomerOrderDialog";
import { DashboardTour } from "@/components/tour/DashboardTour";
import { MfaPromptBanner } from "@/components/auth/MfaPromptBanner";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { Camera } from "lucide-react";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { useRoleFlags } from "@/hooks/use-role-flags";
import { useGridPreference } from "@/hooks/use-grid-preference";
import { useSyncPartnerProfile } from "@/hooks/use-sync-partner-profile";
import { useBrand } from "@/contexts/BrandContext";
import { getBrandLogoStyle } from "@/lib/brand/logoScale";

interface Profile {
  username: string | null;
  avatar_url: string | null;
  first_name: string | null;
  last_name: string | null;
  grid_columns_preference?: number | null;
}

interface DeviceAssignment {
  device_id: string;
  device_details: {
    device_name: string | null;
    device_description: string | null;
  } | null;
}

const Dashboard = () => {
  console.log('📊 [Dashboard] Component rendering started');
  const navigate = useNavigate();
  const { brand, isWhiteLabel } = useBrand();
  const portalLogo = isWhiteLabel ? brand?.config.logo_url : undefined;
  
  // Helper function to generate initials from profile data
  const getInitials = (profile: Profile | null): string => {
    console.log('👤 [Dashboard] Getting initials from profile:', profile);
    
    if (!profile) {
      console.log('👤 [Dashboard] No profile - deriving from user email');
      if (user?.email) {
        const emailInitial = user.email.charAt(0).toUpperCase();
        console.log('👤 [Dashboard] Using email initial:', emailInitial);
        return emailInitial;
      }
      return 'U';
    }
    
    // Try first name and last name initials
    if (profile.first_name && profile.last_name) {
      const initials = `${profile.first_name.charAt(0)}${profile.last_name.charAt(0)}`.toUpperCase();
      console.log('👤 [Dashboard] Using first+last name initials:', initials);
      return initials;
    }
    
    // Try first name initial only
    if (profile.first_name) {
      const initial = profile.first_name.charAt(0).toUpperCase();
      console.log('👤 [Dashboard] Using first name initial:', initial);
      return initial;
    }
    
    // Fall back to username initial
    if (profile.username) {
      const initial = profile.username.charAt(0).toUpperCase();
      console.log('👤 [Dashboard] Using username initial:', initial);
      return initial;
    }
    
    // Email fallback before final U
    if (user?.email) {
      const emailInitial = user.email.charAt(0).toUpperCase();
      console.log('👤 [Dashboard] Using email initial as fallback:', emailInitial);
      return emailInitial;
    }
    
    // Final fallback
    console.log('👤 [Dashboard] No valid data - returning U');
    return 'U';
  };

  const {
    deviceId
  } = useParams();
  const {
    user,
    session,
    signOut
  } = useAuth();
  
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [previousDeviceId, setPreviousDeviceId] = useState<string | null>(null);
  const [customerOrderDialogOpen, setCustomerOrderDialogOpen] = useState(false);
  const [isOrderSectionExpanded, setIsOrderSectionExpanded] = useState(true);
  const [gridColumns, setGridColumns] = useState(3);
  const [profileLoadAttempts, setProfileLoadAttempts] = useState(0);
  const [profileLoadError, setProfileLoadError] = useState<string | null>(null);
  const MAX_PROFILE_LOAD_ATTEMPTS = 3;
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { role, isSystemAdmin } = useSimpleRole();
  const { isDemoOnly, hasSuperuserSurface } = useRoleFlags();
  // Treat internal_staff like a superuser for fleet visibility but filter to demo;
  // advisor also sees demo-only fleet view.
  const showAllDevicesFleet = isSystemAdmin || hasSuperuserSurface || isDemoOnly;
  const { saveGridPreference } = useGridPreference();
  
  // Auto-sync partner profile if needed
  const { syncNeeded, isLoading: isSyncing } = useSyncPartnerProfile();
  
  // Reload profile when sync completes  
  useEffect(() => {
    // Only reload profile after sync if we don't already have one
    if (!isSyncing && user && syncNeeded === false && !profile && profileLoadAttempts < MAX_PROFILE_LOAD_ATTEMPTS) {
      console.log('👤 [Dashboard] Sync completed - reloading profile (attempt', profileLoadAttempts + 1, ')');
      getProfile();
    }
  }, [isSyncing, syncNeeded, user]);
  const {
    data: devices,
    isLoading: isLoadingDevices
  } = useQuery({
    queryKey: ["device-assignments-with-names", isSystemAdmin, isDemoOnly, hasSuperuserSurface],
    queryFn: async () => {
      if (!session) {
        throw new Error("No session");
      }

      console.log('📱 [Dashboard] User role:', role, 'showAllDevicesFleet:', showAllDevicesFleet, 'isDemoOnly:', isDemoOnly);

      if (showAllDevicesFleet) {
        // Superuser / internal_staff / advisor: Get ALL devices then filter for demo-only roles
        const { data: allDevices, error } = await supabase
          .from('device_details')
          .select('device_id, device_name, device_description, device_status')
          .order('device_last_updated', { ascending: false, nullsFirst: false });
        
        if (error) {
          console.error("📱 [Dashboard] Error fetching all devices for superuser:", error);
          throw error;
        }

        const filteredDevices = isDemoOnly
          ? (allDevices || []).filter(d => d.device_status?.toLowerCase() === 'demo')
          : (allDevices || []);

        // Transform to match DeviceAssignment interface
        const formattedDevices = filteredDevices.map(device => ({
          device_id: device.device_id,
          device_details: {
            device_name: device.device_name,
            device_description: device.device_description
          }
        }));
        
        console.log('📱 [Dashboard] All devices formatted:', formattedDevices.length);
        return formattedDevices;
      } else {
        // Regular user: Get only assigned devices
        console.log('📱 [Dashboard] Regular user detected - fetching assigned devices only');
        
        // First get device assignments for the user
        const { data: assignmentData, error: assignmentError } = await supabase
          .from('device_assignments')
          .select('device_id, assigned_at')
          .eq('user_id', session.user.id);
        
        if (assignmentError) {
          console.error("📱 [Dashboard] Error fetching assignments:", assignmentError);
          throw assignmentError;
        }

        if (!assignmentData || assignmentData.length === 0) {
          console.log("📱 [Dashboard] No device assignments found for user");
          return [];
        }

        const deviceIds = assignmentData.map(a => a.device_id);
        console.log("📱 [Dashboard] Found device IDs:", deviceIds);

        // Then get device details for those device IDs
        const { data, error } = await supabase
          .from('device_details')
          .select('device_id, device_name, device_description')
          .in('device_id', deviceIds);
        
        if (error) {
          console.error("📱 [Dashboard] Error fetching device details:", error);
          throw error;
        }

        // Combine assignment data with device details
        const combinedData = assignmentData.map(assignment => {
          const deviceDetails = data?.find(device => device.device_id === assignment.device_id);
          return {
            device_id: assignment.device_id,
            device_details: deviceDetails ? {
              device_name: deviceDetails.device_name,
              device_description: deviceDetails.device_description
            } : null
          };
        }).filter(item => item.device_details); // Only include items where we found device details
        
        console.log("📱 [Dashboard] Combined assigned devices:", combinedData.length);
        return combinedData as DeviceAssignment[];
      }
    },
    enabled: !!session
  });

  // Check if user has any organization roles
  const {
    data: userRoles,
    isLoading: isLoadingRoles
  } = useQuery({
    queryKey: ["user-roles-check"],
    queryFn: async () => {
      if (!session) {
        return [];
      }
      const {
        data,
        error
      } = await supabase.from('user_roles').select('role, organization_id').eq('user_id', session.user.id);
      if (error) {
        console.error("Error fetching user roles:", error);
        throw error;
      }
      return data;
    },
    enabled: !!session
  });
  const currentDevice = devices?.find(d => d.device_id === deviceId);
  const {
    data: currentDeviceDetails
  } = useQuery({
    queryKey: ["device-details", deviceId],
    queryFn: async () => {
      if (!deviceId) return null;
      const {
        data,
        error
      } = await supabase.from('device_details').select('device_description, device_name').eq('device_id', deviceId).single();
      if (error) {
        console.error("Error fetching device details:", error);
        throw error;
      }
      return data;
    },
    enabled: !!deviceId
  });
  useEffect(() => {
    if (deviceId && deviceId !== previousDeviceId) {
      setPreviousDeviceId(deviceId);
    }
  }, [deviceId]);
  useEffect(() => {
    // Only fetch profile when user actually changes (login/logout)
    // Don't refetch during token refresh when user object stays the same
    if (user && !profile && profileLoadAttempts < MAX_PROFILE_LOAD_ATTEMPTS && !profileLoadError) {
      console.log('👤 [Dashboard] Initial user login - fetching profile (attempt', profileLoadAttempts + 1, '):', user.email);
      getProfile();
    }
  }, [user?.id]); // Only trigger on user ID change, not entire user object
  const getProfile = async () => {
    try {
      if (!user) throw new Error("No user found");
      
      console.log('👤 [Dashboard] Fetching profile for user:', user.email, 'attempt:', profileLoadAttempts + 1);
      setProfileLoadAttempts(prev => prev + 1);
      
      const {
        data,
        error
      } = await supabase.from("profiles").select("username, avatar_url, first_name, last_name, grid_columns_preference").eq("user_id", user.id).maybeSingle();
      
      if (error) throw error;
      
      console.log('👤 [Dashboard] Profile data loaded:', data);
      setProfile(data);
      setProfileLoadError(null); // Clear any previous errors
      setProfileLoadAttempts(0); // Reset attempts on success
      
      // Initialize grid columns from preference, fallback to 3 if null/undefined
      if (data?.grid_columns_preference) {
        setGridColumns(data.grid_columns_preference);
      }
    } catch (error: any) {
      console.error("Error loading profile:", error);
      console.error("Error details:", {
        code: error?.code,
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
        attempt: profileLoadAttempts + 1
      });
      
      // Check if it's a JWT expiration error (PGRST301 = JWT expired)
      if (error?.code === 'PGRST301' || error?.message?.includes('JWT') || error?.message?.includes('expired')) {
        console.log('🔴 [Dashboard] JWT expired during profile load - signing out');
        toast.error("Session expired. Please log in again.");
        await signOut();
        return;
      }
      
      // Only show toast if this is not just a temporary auth transition
      // and we haven't exceeded max attempts
      if (error?.message !== "No user found" && error?.code !== 'PGRST116') {
        if (profileLoadAttempts + 1 >= MAX_PROFILE_LOAD_ATTEMPTS) {
          setProfileLoadError(error.message);
          toast.error("Unable to load profile after multiple attempts. Please refresh the page.");
        } else {
          // Only log warning on non-final attempts to avoid spam
          console.warn('⚠️ [Dashboard] Profile load failed, will retry. Attempt:', profileLoadAttempts + 1);
        }
      }
    } finally {
      setLoading(false);
    }
  };
  const handleLogout = async () => {
    try {
      // CRITICAL: Clear ALL cached data before signing out
      // This prevents cross-user data leakage when a new user logs in
      queryClient.clear();
      
      await signOut();
      navigate("/", { state: { justLoggedOut: true } });
      toast.success("Logged out successfully");
    } catch (error) {
      toast.error("Error logging out");
      console.error("Error:", error);
    }
  };
  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: ["device-assignments"]
    });
    queryClient.invalidateQueries({
      queryKey: ["device-assignments-with-names", isSystemAdmin]
    });
    if (deviceId) {
      queryClient.invalidateQueries({
        queryKey: ["device-details", deviceId]
      });
      queryClient.invalidateQueries({
        queryKey: ["device", deviceId]
      });
    }
    if (previousDeviceId) {
      queryClient.invalidateQueries({
        queryKey: ["device-details", previousDeviceId]
      });
      queryClient.invalidateQueries({
        queryKey: ["device", previousDeviceId]
      });
    }
    devices?.forEach(assignment => {
      queryClient.invalidateQueries({
        queryKey: ["device-details", assignment.device_id]
      });
      queryClient.invalidateQueries({
        queryKey: ["device", assignment.device_id]
      });
    });
    toast.success("Refreshing device data...");
  };
  const handleHomeClick = () => {
    handleRefresh();
    setPreviousDeviceId(null);
  };

  // Check if user has no roles/organizations and no devices
  // Only consider them as having no org/devices when loading is complete AND data is empty
  const hasNoOrganization = !isLoadingRoles && userRoles !== undefined && userRoles.length === 0;
  const hasNoDevices = !isLoadingDevices && devices !== undefined && devices.length === 0;
  
  // Show loading state if any critical data is still loading
  const isInitialLoading = isLoadingRoles || isLoadingDevices || loading;
  
  console.log('📊 [Dashboard] Render state:', {
    isLoadingRoles,
    isLoadingDevices,
    loading,
    isInitialLoading,
    hasNoOrganization,
    hasNoDevices,
    devicesCount: devices?.length,
    userRolesCount: userRoles?.length
  });
  
  return <>
      <DeviceAssignmentHandler />
      <SidebarProvider>
        <div className="brand-portal flex min-h-screen w-full bg-background">
          <AppSidebar />
          <main className="flex-1">
             {/* Fixed Header */}
             <FixedPageHeader
               {...(deviceId ? {
                  title: currentDevice?.device_details?.device_description || currentDevice?.device_details?.device_name || deviceId,
                  icon: Camera,
                  actions: !isMobile ? <img src={portalLogo || "https://ik.imagekit.io/SavySnap/Marketing/SSL-Logo.jpg"} alt={`${brand?.config.company_name || "Snapshot Live"} logo`} className={isWhiteLabel ? "brand-header-logo object-contain" : "h-8 max-w-[10rem] object-contain"} style={isWhiteLabel ? getBrandLogoStyle(brand?.config.logo_scale) : undefined} /> : undefined
                } : PAGE_HEADERS.dashboard)}
                avatar={!deviceId ? {
                  src: profile?.avatar_url || undefined,
                  fallback: getInitials(profile)
                } : undefined}
                actions={!deviceId ? (
                  <Button 
                    variant="outline" 
                    className="brand-profile-button gap-2 bg-component-background text-foreground border-border hover:bg-accent hover:text-accent-foreground"
                  >
                    <span className="font-medium">
                      {profile?.first_name && profile?.last_name 
                        ? `${profile.first_name} ${profile.last_name}` 
                        : profile?.username || 'User'}
                    </span>
                    {role && (
                      <span className="text-xs uppercase tracking-wide opacity-90">
                        {role}
                      </span>
                    )}
                  </Button>
                ) : undefined}
             />
             
             {/* Main Content with top padding for fixed header */}
             <div className="px-4 md:px-8 py-6 md:py-8 space-y-8" style={{ paddingTop: "8.5rem" }}>
               {!deviceId && <MfaPromptBanner />}
               
              {deviceId ? <DeviceHistory device_id={deviceId} initialData={currentDevice?.device_details} /> : <>
                  {/* Profile Loading Error with Manual Retry */}
                  {profileLoadError && profileLoadAttempts >= MAX_PROFILE_LOAD_ATTEMPTS && (
                    <div className="max-w-2xl mx-auto p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <h3 className="text-lg font-semibold mb-2">Profile Loading Issue</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        We're having trouble loading your profile. This might be a temporary connection issue.
                      </p>
                      <Button 
                        onClick={() => {
                          setProfileLoadAttempts(0);
                          setProfileLoadError(null);
                          getProfile();
                        }}
                        variant="outline"
                      >
                        Retry Loading Profile
                      </Button>
                    </div>
                  )}
                  
                  {/* Show loading state while data is being fetched */}
                  {isInitialLoading && (
                    <div className="mb-6 p-6 text-center">
                      <div className="animate-pulse space-y-4">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                      </div>
                    </div>
                  )}
                  
                  {/* Only show content when loading is complete */}
                  {!isInitialLoading && (
                    <>
                      {hasNoOrganization && hasNoDevices && <div className="mb-6 p-6 bg-blue-50 rounded-lg border border-blue-100 text-center">
                          <h3 className="text-lg font-medium mb-2">Welcome to Snapshot Live!</h3>
                          <p className="text-muted-foreground mb-4">
                            You don't have any devices or organizations set up yet.
                          </p>
                          <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button onClick={() => navigate("/dashboard/admin")} className="flex items-center gap-2">
                              <PlusCircle className="h-4 w-4" />
                              Create Organization
                            </Button>
                          </div>
                        </div>}
                      
                      {/* Customer Order Creation Section - Hidden on mobile */}
                      {!hasNoOrganization && !isMobile && <div className="hidden mb-6 p-6 rounded-lg border-teal-light cursor-pointer transition-all duration-300 hover:shadow-lg" style={{ backgroundColor: "#F0EDE6" }} onClick={() => setIsOrderSectionExpanded(!isOrderSectionExpanded)}>
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-left" style={{ color: "#321521" }}>Need Cameras or Images?</h3>
                            <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${isOrderSectionExpanded ? 'rotate-180' : ''}`} style={{ color: "#321521" }} />
                          </div>
                          <div className={`overflow-hidden transition-all duration-300 ${isOrderSectionExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-4">
                              <div className="md:flex-1">
                                <p className="mb-4" style={{ color: "#321521" }}>
                                  Create your own custom order proposal with cameras and image packages tailored to your needs.
                                </p>
                              </div>
                               <Button onClick={(e) => { e.stopPropagation(); setCustomerOrderDialogOpen(true); }} className="w-full md:w-auto flex items-center justify-center gap-2 text-black hover:opacity-80 px-8 py-4 text-lg" style={{ backgroundColor: "#DFEF97" }}>
                                 <div className="rounded-full p-1.5" style={{ backgroundColor: "#DFEF97" }}>
                                   <PlusCircle className="h-3 w-3 text-black stroke-2" />
                                 </div>
                                 Create Order
                               </Button>
                            </div>
                          </div>
                        </div>}
                    </>
                  )}
                  
                   <DeviceGrid 
                     gridColumns={gridColumns} 
                     onGridColumnsChange={(columns) => {
                       setGridColumns(columns);
                       saveGridPreference(columns);
                     }} 
                   />
                </>}
            </div>
          </main>
        </div>
      </SidebarProvider>
      
      {/* Customer Order Dialog */}
      <CustomerOrderDialog open={customerOrderDialogOpen} onOpenChange={setCustomerOrderDialogOpen} />
      
      {/* Dashboard Tour - Always render to handle manual restarts */}
      <DashboardTour />
    </>;
};

export default Dashboard;
